import { Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { instanceApis } from '../../../apis/instanceApis';
import { serverApis } from '../../../apis/serverApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import SettingsTabs from '../../../components/page-sections/settings/SettingsTabs';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { useServer } from '../../../contexts/ServerContext';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { sentenceCase } from '../../../utils/utils';

const ServerSettingsPage = () => {
  const navigate = useNavigate();
  const { loading, server, refreshServer } = useServer();
  const { projectId, gpsId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const handleDelete = async () => {
    try {
      // delete instance
      await serverApis.deleteServer(projectId, gpsId);

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('instance', { ns: 'instance' }),
          })
        )
      );
      const projectOverview = buildPath({
        path: PRIVATE_ROUTE.PROJECT_OVERVIEW,
        params: { projectId: projectId },
      });
      setTimeout(() => navigate(projectOverview), 800);
    } catch (errors) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('server', { ns: 'gps' }),
          })
        )
      );
    }
  };

  const handleRemoveImage = async () => {
    await instanceApis.deleteInstanceImage(projectId, gpsId);
    await refreshServer();
  };

  const handleUpdateServer = async (data) => {
    if (data.icon) {
      const { icon, ...form } = data;
      const formData = new FormData();
      formData.append('image', icon);
      await instanceApis.updateInstanceImage(projectId, gpsId, formData);
      await serverApis.updateInstance(projectId, gpsId, form);
    } else {
      await serverApis.updateInstance(projectId, gpsId, data);
    }
    await refreshServer();
  };

  const handleUpdateCidrs = async (cidrList) => {
    const requestBody = {
      name: server.name,
      description: server.description,
      subdomain: server.subdomain,
      clusterType: server.clusterType,
      cidrs: cidrList,
    };

    await serverApis.updateServer(projectId, gpsId, requestBody);
    await refreshServer();
  };

  if (loading) return <Loading />;

  if (!server)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('settingsPage', {
              ns: 'project',
              text: t('server', { ns: 'server' }),
            }),
          })
        )}
      />
    );

  return (
    <Container>
      <Breadcrumbs serverName={server.name} />
      <TitlePanel title={t('settings', { ns: 'titles' })} />
      <SettingsTabs
        instance={server}
        onDeleteInstance={handleDelete}
        onRemoveImage={handleRemoveImage}
        onUpdateCidrs={handleUpdateCidrs}
        onUpdateInstance={handleUpdateServer}
      />
    </Container>
  );
};

export default ServerSettingsPage;
